import { lazy } from '@deqo/foundation';
import { Matrix } from '../../../../platform/geom/Matrix';
import { Polyline } from '../../../../platform/geom/Polyline';
import { Rect } from '../../../../platform/geom/Rect';
import { Camera } from '../../Camera';
import { Physical, RelativeTo, TransformHints } from '../../entities/Physical';
import { ScaleChildren } from '../../entities/ScaleChildren';
import { Net, NetPart } from '../../nets/Net';
import { Pose } from '../../visualization/Pose';
import { TransformHandle } from './TransformHandle';

// TODO:
// Refactor this so that TransformHints has a separate "border" hint.
const shouldShowBorder = (hints: TransformHints) => {
    if (!hints.visible) return false;
    if (hints.visible && hints.translate) return true;
    if (hints.visible && hints.scaleX) return true;
    if (hints.visible && hints.scaleY) return true;
    if (hints.visible && hints.rotate) return true;
    if (hints.visible && hints.tweaks) return false; // Yes, really
    if (hints.visible && !Object.keys(hints).find(x => x !== 'visible' && !!hints[x])) return true;
};

const normalizeHints = (elmts: Physical[]): TransformHints => {
    // Just one & unlocked? Return its hints
    if (elmts.length === 1 && !elmts[0].locked) {
        return {
            scaleMode: 'free',
            ...elmts[0].queryTransformHints(true),
        };
    }
    // In sets, trues override falses.  So if there is one scalable object, the net becomes scalable.  The exception is if there is ANY
    // locked objects, the whole selection becomes untransformable.
    const merged = {} as any;
    for (const e of elmts) {
        if (e.locked) {
            return TransformHints.off;
        }

        const th = e.locked ? TransformHints.off : e.queryTransformHints(false);
        merged.scaleX = th.scaleX || (merged.scaleX ?? false);
        merged.scaleY = th.scaleY || (merged.scaleY ?? false);
        merged.scaleMode = (th.scaleMode ?? 'free') === 'fixed' ? 'fixed' : (merged.scaleMode ?? 'free');
        merged.rotate = th.rotate || (merged.rotate ?? false);
        merged.translate = th.translate || (merged.translate ?? false);
        merged.visible = th.visible || (merged.visible ?? false);
        // Tweaks are only for single elmts
        merged.tweaks = undefined;
    }
    return TransformHints(merged);
};

const computeFromPhysicals = (camera: Camera, subjects: Physical[]) => {

    // Remove any being tweened
    subjects = subjects.filter(x => !Pose.getEffect(x, 'tween'));

    // Expand any with scale children
    subjects = subjects.flatMap(x => x.is(ScaleChildren) ? [x, ...x.polarize(Physical)] : [x]);

    if (subjects.length === 0) {
        return null;
    }

    const hints = normalizeHints(subjects);
    const rmm = computeRelativeToModelMatrix(subjects);

    // Order here matters as the z-order determines priority.

    const parts = [] as NetPart[];

    if (shouldShowBorder(hints)) {
        parts.push(NetPart.border(
            'border',
            'transform',
            Polyline.from(rmm.apply(new Rect(-.5, -.5, 1, 1)), true).points
        ));
    }

    if (hints.tweaks && subjects.length === 1) {
        parts.push(
            ...hints.tweaks.map(x => TransformHandle.tweak(subjects[0], camera, x.offset, x.style))
        );
    }

    if (hints.scaleX && hints.scaleY) {
        const fixed = hints.scaleMode === 'fixed';
        parts.push(
            TransformHandle.scaleCorner('northEast', camera, rmm, fixed),
            TransformHandle.scaleCorner('northWest', camera, rmm, fixed),
            TransformHandle.scaleCorner('southEast', camera, rmm, fixed),
            TransformHandle.scaleCorner('southWest', camera, rmm, fixed),
        );
    }

    if (hints.scaleX && hints.scaleMode === 'free') {
        parts.push(
            TransformHandle.scaleEdge('east', camera, rmm),
            TransformHandle.scaleEdge('west', camera, rmm),
        );
    }

    if (hints.scaleY && hints.scaleMode === 'free') {
        parts.push(
            TransformHandle.scaleEdge('north', camera, rmm),
            TransformHandle.scaleEdge('south', camera, rmm),
        );
    }

    if (hints.rotate) {
        parts.push(
            TransformHandle.rotate('northEast', camera, rmm),
            TransformHandle.rotate('northWest', camera, rmm),
            TransformHandle.rotate('southEast', camera, rmm),
            TransformHandle.rotate('southWest', camera, rmm),
        );
    }

    return new TransformNet(parts, hints.visible);
};

const computeRelativeToModelMatrix = (selection: Physical[]) => {

    const [r1, ...rn] = selection.map(x => x.rotation(RelativeTo.Model));
    const uniformRotation = rn.every(x => x === r1);

    // TODO: Eventually support this for more than one object
    if (uniformRotation && r1 !== 0 && selection.length === 1) {

        const points = selection.map(x => x.outline(RelativeTo.Model).points).reduce((t, x) => t.concat(x), []);
        const origin = Rect.fromPoints(points).center();

        const undoRotation = Matrix.identity
            .translate(origin)
            .rotate(-r1)
            .translate(origin.multiply(-1));

        const normalPoints = undoRotation.apply(points);

        return Matrix.identity
            .translate(origin)
            .rotate(r1)
            .scale(Rect.fromPoints(normalPoints).size());

    }
    else {

        const bounds = Rect.fromMany(selection.map(x => x.bounds(RelativeTo.Model)));

        const origin = bounds.center();
        return Matrix.identity
            .translate(origin)
            .scale(bounds.size());
    }
};


export class TransformNet implements Net {

    public static compute(camera: Camera, elmts: Physical[]): TransformNet | null {
        return computeFromPhysicals(camera, elmts);
    }

    public readonly type = 'transform';

    constructor(
        public readonly parts: NetPart[],
        public readonly visible: boolean,
    ) { }

    @lazy()
    public get bounds() {
        return Rect.fromPoints(this.parts.flatMap(x => x.verticies));
    }

    @lazy()
    public get verticies() {
        const points = this.parts.flatMap(x => x.verticies);
        if (!points.length) return null;
        return Polyline.fromHull(points);
    }
}
